import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiX, FiPlay, FiPause, FiVolume2, FiMaximize, FiVolumeX } = FiIcons;

const VideoModal = ({ isOpen, onClose }) => {
  const [isPlaying, setIsPlaying] = React.useState(false);
  const [isMuted, setIsMuted] = React.useState(true);
  const [currentTime, setCurrentTime] = React.useState(0);
  const [duration, setDuration] = React.useState(0);
  const videoRef = React.useRef(null);

  React.useEffect(() => {
    if (isOpen && videoRef.current) {
      videoRef.current.currentTime = 0;
      setCurrentTime(0);
    }
  }, [isOpen]);

  const togglePlay = () => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  const toggleMute = () => {
    if (videoRef.current) {
      videoRef.current.muted = !isMuted;
      setIsMuted(!isMuted);
    }
  };

  const handleTimeUpdate = () => {
    if (videoRef.current) {
      setCurrentTime(videoRef.current.currentTime);
    }
  };

  const handleLoadedMetadata = () => {
    if (videoRef.current) {
      setDuration(videoRef.current.duration);
    }
  };

  const handleSeek = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    const newTime = (clickX / rect.width) * duration;
    
    if (videoRef.current) {
      videoRef.current.currentTime = newTime;
      setCurrentTime(newTime);
    }
  };

  const formatTime = (time) => {
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="absolute inset-0 bg-black/90 backdrop-blur-sm"
            onClick={onClose}
          />
          <motion.div
            initial={{ opacity: 0, scale: 0.8, y: 50 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.8, y: 50 }}
            transition={{ duration: 0.3 }}
            className="relative bg-black rounded-2xl overflow-hidden m-4 max-w-5xl w-full max-h-[95vh] border-2 border-purple-500/30"
          >
            {/* Close button */}
            <button
              onClick={onClose}
              className="absolute top-4 right-4 z-20 bg-black/70 text-white p-3 rounded-full hover:bg-black/90 transition-colors backdrop-blur-sm"
            >
              <SafeIcon icon={FiX} className="text-xl" />
            </button>

            {/* Video container */}
            <div className="relative aspect-video bg-black">
              {/* Actual HTML5 Video with realistic content */}
              <video
                ref={videoRef}
                className="w-full h-full object-cover"
                onPlay={() => setIsPlaying(true)}
                onPause={() => setIsPlaying(false)}
                onTimeUpdate={handleTimeUpdate}
                onLoadedMetadata={handleLoadedMetadata}
                onEnded={() => setIsPlaying(false)}
                muted={isMuted}
                playsInline
                poster="data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 1200 675'%3E%3Cdefs%3E%3ClinearGradient id='bg' x1='0%25' y1='0%25' x2='100%25' y2='100%25'%3E%3Cstop offset='0%25' stop-color='%23581c87'/%3E%3Cstop offset='100%25' stop-color='%23be185d'/%3E%3C/linearGradient%3E%3C/defs%3E%3Crect width='1200' height='675' fill='url(%23bg)'/%3E%3Ctext x='600' y='320' text-anchor='middle' fill='white' font-size='48' font-family='Arial, sans-serif' font-weight='bold'%3EGhost Kitchen Success Story%3C/text%3E%3Ctext x='600' y='380' text-anchor='middle' fill='%23e879f9' font-size='24' font-family='Arial, sans-serif'%3EFrom $0 to $2M Revenue in 18 Months%3C/text%3E%3Ccircle cx='600' cy='450' r='30' fill='white' opacity='0.8'/%3E%3Cpolygon points='590,440 590,460 610,450' fill='%23581c87'/%3E%3C/svg%3E"
              >
                {/* Using a sample video - you can replace with actual ghost kitchen demo video */}
                <source src="https://sample-videos.com/zip/10/mp4/SampleVideo_1280x720_1mb.mp4" type="video/mp4" />
                
                {/* Fallback content for browsers that don't support video */}
                Your browser does not support the video tag.
              </video>

              {/* Play button overlay when paused */}
              {!isPlaying && (
                <motion.div
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.8 }}
                  className="absolute inset-0 flex items-center justify-center bg-black/30 cursor-pointer"
                  onClick={togglePlay}
                >
                  <div className="bg-white/20 backdrop-blur-sm rounded-full p-6 border border-white/30 hover:bg-white/30 transition-all duration-300">
                    <SafeIcon icon={FiPlay} className="text-white text-4xl ml-1" />
                  </div>
                </motion.div>
              )}

              {/* Video controls */}
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/90 via-black/50 to-transparent p-6">
                {/* Progress bar */}
                <div 
                  className="mb-4 bg-white/20 h-2 rounded-full overflow-hidden cursor-pointer group"
                  onClick={handleSeek}
                >
                  <div 
                    className="h-full bg-gradient-to-r from-purple-500 to-pink-500 transition-all duration-100 relative"
                    style={{ width: duration ? `${(currentTime / duration) * 100}%` : '0%' }}
                  >
                    <div className="absolute right-0 top-1/2 transform translate-x-1/2 -translate-y-1/2 w-4 h-4 bg-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity shadow-lg" />
                  </div>
                </div>

                <div className="flex items-center justify-between text-white">
                  <div className="flex items-center space-x-4">
                    <button
                      onClick={togglePlay}
                      className="bg-purple-600 hover:bg-purple-700 p-3 rounded-full transition-colors shadow-lg"
                    >
                      <SafeIcon icon={isPlaying ? FiPause : FiPlay} className="text-lg" />
                    </button>
                    
                    <button
                      onClick={toggleMute}
                      className="hover:text-purple-300 transition-colors p-2"
                    >
                      <SafeIcon icon={isMuted ? FiVolumeX : FiVolume2} className="text-lg" />
                    </button>

                    <span className="text-sm font-mono">
                      {formatTime(currentTime)} / {formatTime(duration)}
                    </span>
                  </div>
                  
                  <div className="flex items-center space-x-4">
                    <span className="text-sm bg-black/50 px-3 py-1 rounded-full backdrop-blur-sm">
                      Ghost Kitchen Success Demo
                    </span>
                    <button className="hover:text-purple-300 transition-colors p-2">
                      <SafeIcon icon={FiMaximize} className="text-lg" />
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Video description */}
            <div className="p-8 bg-gradient-to-r from-slate-800 to-purple-900">
              <div className="grid md:grid-cols-2 gap-8">
                <div>
                  <h3 className="text-2xl font-bold text-white mb-3">
                    Real Ghost Kitchen Success Story
                  </h3>
                  <p className="text-gray-300 text-sm mb-6 leading-relaxed">
                    Watch Sarah Chen's incredible journey from startup to $2M ARR in just 18 months. 
                    Learn the exact strategies, challenges overcome, and growth tactics that made 
                    Noodle Dynasty a ghost kitchen success story.
                  </p>
                  
                  <div className="grid grid-cols-3 gap-4 mb-6">
                    <div className="text-center bg-white/10 rounded-lg p-3">
                      <div className="text-xl font-bold text-green-400">$2M</div>
                      <div className="text-xs text-gray-400">Annual Revenue</div>
                    </div>
                    <div className="text-center bg-white/10 rounded-lg p-3">
                      <div className="text-xl font-bold text-blue-400">18</div>
                      <div className="text-xs text-gray-400">Months</div>
                    </div>
                    <div className="text-center bg-white/10 rounded-lg p-3">
                      <div className="text-xl font-bold text-purple-400">5</div>
                      <div className="text-xs text-gray-400">Locations</div>
                    </div>
                  </div>
                </div>

                <div>
                  <h4 className="text-lg font-semibold text-white mb-4">What You'll Learn:</h4>
                  <ul className="space-y-3 text-gray-300 text-sm">
                    <li className="flex items-start">
                      <span className="text-purple-400 mr-2">•</span>
                      How to choose the perfect ghost kitchen location
                    </li>
                    <li className="flex items-start">
                      <span className="text-purple-400 mr-2">•</span>
                      Multi-brand strategy that tripled revenue
                    </li>
                    <li className="flex items-start">
                      <span className="text-purple-400 mr-2">•</span>
                      Data-driven menu optimization techniques
                    </li>
                    <li className="flex items-start">
                      <span className="text-purple-400 mr-2">•</span>
                      Scaling operations across multiple cities
                    </li>
                    <li className="flex items-start">
                      <span className="text-purple-400 mr-2">•</span>
                      Partnership strategies with delivery platforms
                    </li>
                  </ul>
                </div>
              </div>
              
              <div className="flex flex-wrap gap-2 mt-6 pt-6 border-t border-white/10">
                {['Case Study', 'Revenue Growth', 'Multi-Brand Strategy', 'Scaling Tips', 'Real Results'].map((tag) => (
                  <span 
                    key={tag}
                    className="bg-purple-600/20 text-purple-300 px-3 py-1 rounded-full text-xs font-medium"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};

export default VideoModal;